import React, { useMemo, useState } from "react";
import { productionApi } from "../../../services/reportsService";
import { useReportData } from "../useReportData";
import ServerTable from "../ServerTable";
import {
  AlertStrip,
  BarList,
  DonutChart,
  ErrorState,
  ItemList,
  KpiGrid,
  Leaderboard,
  Panel,
  ReportSkeleton,
  Row,
  SeriesLegend,
  StatusBadge,
  TrendChart,
  TablePanel,
  DistTable,
  TrendTable,
  cols,
  useReportContext,
} from "../ReportKit";
import {
  PALETTE,
  applyPrevious,
  buildKpis,
  formatDate,
  formatNumber,
  getPreviousRange,
  numify,
  num,
  toDist,
} from "../reportUtils";

const DEFS = [
  {
    key: "totalWarehouses",
    label: "Warehouses",
    desc: "Places where stock is kept",
    icon: "mdi:warehouse",
  },
  {
    key: "totalStockItems",
    label: "Different items",
    desc: "Products and materials in stock",
    icon: "mdi:package-variant-closed",
  },
  {
    key: "totalQuantityOnHand",
    label: "Units in stock",
    desc: "Everything available right now",
    icon: "mdi:cube-outline",
  },
  {
    key: "totalStockValue",
    label: "Stock worth",
    desc: "Money value of all stock",
    unit: "CURRENCY",
    icon: "mdi:cash-multiple",
  },
  {
    key: "capacityUtilizationPct",
    label: "Space used",
    desc: "How full the warehouses are",
    unit: "PERCENT",
    icon: "mdi:gauge",
  },
  {
    key: "inboundQuantity",
    label: "Stock came in",
    desc: "Units received {p}",
    icon: "mdi:truck-delivery-outline",
  },
  {
    key: "outboundQuantity",
    label: "Stock went out",
    desc: "Units sent out {p}",
    icon: "mdi:truck-fast-outline",
  },
  {
    key: "netMovement",
    label: "Stock change",
    desc: "Came in minus went out {p}",
    icon: "mdi:swap-vertical",
  },
  {
    key: "lowStockCount",
    label: "Running low",
    desc: "Items that are almost finished",
    good: "DOWN",
    icon: "mdi:package-down",
  },
  {
    key: "outOfStockCount",
    label: "Finished items",
    desc: "Items with nothing left",
    good: "DOWN",
    icon: "mdi:package-variant-remove",
  },
];
const MOVEMENT_KEYS = ["inboundQuantity", "outboundQuantity", "netMovement"]; // only these have a period-on-period meaning

const stockLabel = (s) =>
  ({ OK: "In stock", LOW: "Low stock", OUT: "Out of stock" })[
    String(s).toUpperCase()
  ] || s;

const WarehouseView = ({ params, refreshKey }) => {
  const { view } = useReportContext();
  const [tab, setTab] = useState("levels");
  const dates = { startDate: params.startDate, endDate: params.endDate };
  const { data, loading, error, reload } = useReportData(async () => {
    const [cur, prev] = await Promise.all([
      productionApi.warehouseSummary(dates),
      productionApi.warehouseSummary(getPreviousRange(dates)).catch(() => null),
    ]);
    return { cur, prev };
  }, [params.startDate, params.endDate, refreshKey]);
  const tableParams = useMemo(() => dates, [params.startDate, params.endDate]); // eslint-disable-line

  if (error) return <ErrorState message={error} onRetry={reload} />;
  if (!data) return <ReportSkeleton />;

  const { cur, prev } = data;
  const base = buildKpis(cur.kpis, DEFS);
  const withPrev = applyPrevious(
    base.filter((k) => MOVEMENT_KEYS.includes(k.key)),
    prev?.kpis,
  );
  const kpis = base.map((k) => withPrev.find((p) => p.key === k.key) || k);
  const low = Number(cur.kpis?.lowStockCount) || 0;
  const out = Number(cur.kpis?.outOfStockCount) || 0;
  const flow = [
    { key: "inbound", label: "Received", color: PALETTE[1], type: "bar" },
    { key: "outbound", label: "Issued", color: PALETTE[4], type: "bar" },
    { key: "netBalance", label: "Net", color: PALETTE[0], type: "line" },
  ];

  const levelColumns = [
    { key: "warehouseName", header: "Warehouse" },
    { key: "stockName", header: "Item", render: (v) => <strong>{v}</strong> },
    { key: "stockType", header: "Type" },
    { key: "rackNumber", header: "Rack" },
    {
      key: "quantity",
      header: "Quantity",
      align: "right",
      render: (v) => formatNumber(v),
    },
    {
      key: "reorderLevel",
      header: "Reorder at",
      align: "right",
      render: (v) => formatNumber(v),
    },
    {
      key: "status",
      header: "Status",
      render: (v) => <StatusBadge value={stockLabel(v)} />,
      exportValue: (r) => stockLabel(r.status),
    },
    {
      key: "lastMovementAt",
      header: "Last movement",
      render: (v) => formatDate(v, true),
    },
  ];
  const moveColumns = [
    { key: "createdAt", header: "When", render: (v) => formatDate(v, true) },
    { key: "warehouseName", header: "Warehouse" },
    { key: "stockName", header: "Item", render: (v) => <strong>{v}</strong> },
    { key: "stockType", header: "Type" },
    { key: "rackNumber", header: "Rack" },
    {
      key: "transactionType",
      header: "Transaction",
      render: (v) => <StatusBadge value={v} />,
    },
    {
      key: "quantityChanged",
      header: "Changed",
      align: "right",
      render: (v) => (
        <span className={Number(v) < 0 ? "rp-neg" : "rp-pos"}>
          {Number(v) > 0 ? "+" : ""}
          {formatNumber(v)}
        </span>
      ),
    },
    {
      key: "closingBalance",
      header: "Balance",
      align: "right",
      render: (v) => formatNumber(v),
    },
    { key: "referenceNo", header: "Reference" },
    { key: "updatedByEmployeeName", header: "Updated by" },
  ];

  const lv = (title, how, filter) => ({
    modal: {
      title,
      how,
      columns: levelColumns,
      fetcher: productionApi.warehouseStockLevels,
      params: {},
      ...(filter ? { filter } : {}),
    },
  });
  const mv = (title, how, filter) => ({
    modal: {
      title,
      how,
      columns: moveColumns,
      fetcher: productionApi.warehouseMovements,
      params: dates,
      ...(filter ? { filter } : {}),
    },
  });
  const details = {
    totalWarehouses: { scrollTo: "warehouses" },
    totalStockItems: lv(
      "All stock items",
      "These are all the items kept in your warehouses.",
    ),
    totalQuantityOnHand: lv(
      "Units in stock",
      "Add up the units of every item here to get the total in stock.",
    ),
    totalStockValue: lv(
      "Items that make up the stock worth",
      "The money value of these items adds up to the stock worth.",
    ),
    capacityUtilizationPct: { scrollTo: "warehouses" },
    inboundQuantity: mv(
      "Stock that came in",
      "These movements brought stock in {p}.",
      (r) => Number(r.quantityChanged) > 0,
    ),
    outboundQuantity: mv(
      "Stock sent out",
      "These movements sent stock out {p}.",
      (r) => Number(r.quantityChanged) < 0,
    ),
    netMovement: mv(
      "All stock movements",
      "Stock that came in minus stock that went out {p}. These are all the movements.",
    ),
    lowStockCount: {
      scrollTo: "low-stock",
      ...lv(
        "Items running low",
        "These items are at or below the level where you should order again.",
        (r) => /LOW|OUT/i.test(r.status || ""),
      ),
    },
    outOfStockCount: lv(
      "Items with nothing left",
      "These items have no stock left.",
      (r) => /OUT/i.test(r.status || ""),
    ),
  };

  return (
    <div className={`rp-view ${loading ? "rp-refreshing" : ""}`}>
      <AlertStrip
        items={[
          out > 0 && {
            tone: "bad",
            icon: "mdi:package-variant-remove",
            text: `${out} items are out of stock`,
          },
          low > 0 && {
            tone: "warn",
            icon: "mdi:package-down",
            text: `${low} items are at or below their reorder level`,
          },
        ]}
      />
      <KpiGrid kpis={kpis} details={details} />
      {view === "table" ? (
        <>
          <div className="rp-subtabs" role="tablist">
            <button
              type="button"
              role="tab"
              aria-selected={tab === "levels"}
              className={tab === "levels" ? "active" : ""}
              onClick={() => setTab("levels")}
            >
              Stock levels
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={tab === "moves"}
              className={tab === "moves" ? "active" : ""}
              onClick={() => setTab("moves")}
            >
              Movements
            </button>
          </div>
          {tab === "levels" ? (
            <ServerTable
              key="levels"
              title="Stock levels"
              columns={levelColumns}
              params={{}}
              fetcher={productionApi.warehouseStockLevels}
              searchable={false}
              sortable={false}
              pageSize={25}
              exportName="stock-levels"
            />
          ) : (
            <ServerTable
              key="moves"
              title="Stock movements"
              columns={moveColumns}
              params={tableParams}
              fetcher={productionApi.warehouseMovements}
              searchable={false}
              sortable={false}
              pageSize={25}
              exportName="stock-movements"
            />
          )}

          <div className="rp-tables">
            <TrendTable
              title="Stock in and out by date"
              data={numify(
                (cur.movementTrend || []).map((r) => ({
                  ...r,
                  bucket: r.date,
                })),
              )}
              series={flow}
              granularity="DAY"
            />
            <TablePanel
              id="warehouses"
              title="Warehouses"
              exportName="warehouses"
              columns={[
                cols.text("name", "Warehouse"),
                cols.num("qty", "Units in stock"),
                cols.num("cap", "Capacity"),
                cols.num("pct", "Space used", "PERCENT"),
              ]}
              rows={(cur.quantityByWarehouse || []).map((w) => ({
                name: w.warehouseName,
                qty: w.quantity,
                cap: w.capacity,
                pct: w.utilizationPct,
              }))}
            />
            <DistTable
              title="Warehouse status"
              data={toDist(cur.warehouseStatus, ["count"])}
              nameHeader="Status"
              valueHeader="Warehouses"
            />
            <DistTable
              title="Kinds of stock movement"
              data={toDist(cur.transactionTypes, ["count"])}
              nameHeader="Kind"
              valueHeader="Times"
            />
            <TablePanel
              title="Largest stock holdings"
              exportName="top-stock"
              columns={[
                cols.text("stockName", "Item"),
                cols.text("stockType", "Type"),
                cols.num("quantity", "Units"),
              ]}
              rows={cur.topStockItems || []}
            />
            <TablePanel
              title="Fast movers"
              exportName="fast-movers"
              columns={[
                cols.text("stockName", "Item"),
                cols.num("outboundQuantity", "Units sent out"),
              ]}
              rows={cur.fastMovers || []}
            />
            <TablePanel
              title="Stock by category"
              exportName="stock-categories"
              columns={[
                cols.text("category", "Category"),
                cols.text("stockType", "Type"),
                cols.num("count", "Items"),
                cols.num("quantity", "Units"),
              ]}
              rows={cur.stockByCategory || []}
            />
            <TablePanel
              id="low-stock"
              title="Running low"
              exportName="running-low"
              columns={[
                cols.text("stockName", "Item"),
                cols.text("warehouseName", "Warehouse"),
                cols.num("quantity", "Units left"),
                cols.num("reorderLevel", "Order again at"),
                cols.num("daysOfCover", "Days left"),
              ]}
              rows={cur.lowStockItems || []}
            />
            <TablePanel
              title="Not moving"
              exportName="slow-movers"
              columns={[
                cols.text("stockName", "Item"),
                cols.date("lastOutboundDate", "Last sent out"),
                cols.num("daysSinceLastOutbound", "Days idle"),
              ]}
              rows={cur.slowMovers || []}
            />
            <TablePanel
              title="Who updates stock"
              exportName="stock-employees"
              columns={[
                cols.text("stockUpdatedByEmployeeName", "Person"),
                cols.num("transactionCount", "Updates"),
                cols.num("totalQuantityChanged", "Units moved"),
              ]}
              rows={cur.movementByEmployee || []}
            />
          </div>
        </>
      ) : (
        <>
          <Row layout="2-1">
            <Panel
              title="Stock movement"
              subtitle="Received and issued"
              actions={<SeriesLegend series={flow} />}
            >
              <TrendChart
                data={numify(
                  (cur.movementTrend || []).map((r) => ({
                    ...r,
                    bucket: r.date,
                  })),
                )}
                series={flow}
                granularity="DAY"
              />
            </Panel>
            <Panel title="Transaction types">
              <DonutChart data={toDist(cur.transactionTypes, ["count"])} />
            </Panel>
          </Row>

          <Row layout="1-1-1">
            <Panel id="warehouses" title="Quantity by warehouse">
              <BarList
                color={PALETTE[0]}
                data={(cur.quantityByWarehouse || []).map((w) => ({
                  name: w.warehouseName,
                  value: Number(w.quantity) || 0,
                }))}
              />
            </Panel>
            <Panel
              title="Capacity used"
              subtitle="Share of each warehouse's capacity"
            >
              <BarList
                unit="PERCENT"
                max={100}
                color={PALETTE[2]}
                data={(cur.quantityByWarehouse || []).map((w) => ({
                  name: w.warehouseName,
                  value: Number(w.utilizationPct) || 0,
                }))}
              />
            </Panel>
            <Panel title="Warehouse status">
              <DonutChart data={toDist(cur.warehouseStatus, ["count"])} />
            </Panel>
          </Row>

          <Row layout="1-1-1">
            <Panel title="Largest stock holdings">
              <BarList
                color={PALETTE[3]}
                data={(cur.topStockItems || []).map((s) => ({
                  name: s.stockName,
                  value: Number(s.quantity) || 0,
                }))}
              />
            </Panel>
            <Panel title="Fast movers" subtitle="Most issued this period">
              <BarList
                color={PALETTE[1]}
                data={(cur.fastMovers || []).map((s) => ({
                  name: s.stockName,
                  value: Number(s.outboundQuantity) || 0,
                }))}
              />
            </Panel>
            <Panel title="By category">
              <BarList
                color={PALETTE[5]}
                data={(cur.stockByCategory || []).map((c) => ({
                  name: [c.category, c.stockType].filter(Boolean).join(", "),
                  value: Number(c.quantity) || 0,
                }))}
              />
            </Panel>
          </Row>

          <Row layout="1-1">
            <Panel
              id="low-stock"
              title="Running low"
              subtitle="At or below reorder level"
            >
              <ItemList
                empty="Nothing is running low"
                items={(cur.lowStockItems || []).map((s, i) => ({
                  id: `${s.stockId}-${i}`,
                  title: s.stockName,
                  subtitle: `${s.warehouseName || "—"}, reorder at ${formatNumber(s.reorderLevel)}`,
                  badge: Number(s.quantity) <= 0 ? "Out of stock" : "Low stock",
                  right: `${formatNumber(s.quantity)} left${num(s, "daysOfCover") !== null ? `, ${s.daysOfCover}d cover` : ""}`,
                }))}
              />
            </Panel>
            <Panel title="Not moving" subtitle="Longest since last issue">
              <ItemList
                empty="No slow movers"
                items={(cur.slowMovers || []).map((s, i) => ({
                  id: `${s.stockId}-${i}`,
                  title: s.stockName,
                  subtitle: s.lastOutboundDate
                    ? `Last issued ${formatDate(s.lastOutboundDate)}`
                    : "Never issued",
                  right:
                    s.daysSinceLastOutbound != null
                      ? `${s.daysSinceLastOutbound} days idle`
                      : "",
                }))}
              />
            </Panel>
          </Row>

          <Panel title="Who updates stock" subtitle="Transactions by employee">
            <Leaderboard
              primaryLabel="transactions"
              rows={(cur.movementByEmployee || []).map((e) => ({
                name: e.stockUpdatedByEmployeeName,
                primary: Number(e.transactionCount) || 0,
                metrics: [
                  { label: "Units moved", value: e.totalQuantityChanged },
                ],
              }))}
            />
          </Panel>
        </>
      )}
    </div>
  );
};

export default WarehouseView;
